import React, { useEffect, useState } from "react";
import {
  Card,
  Container,
  Grid,
  Tabs,
  Tab,
  useMediaQuery,
  Fade
} from "@mui/material";
import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";
import Breadcrumbs from "examples/Breadcrumbs";
import WhatsAppFloat from "components/WhatsAppFloat";
import { useTheme } from "@mui/material/styles";
import bgBanner from "assets/images/bg-presentation.jpg";
import AssignmentIcon from '@mui/icons-material/Assignment';
import WorkIcon from '@mui/icons-material/Work';

import SectionContainer from "./components/SectionContainer";
import FadeIn from "./components/FadeIn";
import CompanyInfoCard from "./components/CompanyInfoCard";
import RFQForm from "./components/RQFForm";
import CareersForm from "./components/CareersForm";
import { BRAND } from "./constants";

export default function Contato() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const parallax = isMobile ? "scroll" : "fixed";
  const NAVBAR_PT = { xs: 18, md: 25 };
  const [loaded, setLoaded] = useState(false);
  const [tab, setTab] = useState(0);

  useEffect(() => {
    const t = setTimeout(() => setLoaded(true), 120);
    return () => clearTimeout(t);
  }, []);

  const handleTabChange = (_, newValue) => {
    setTab(newValue);
  };

  return (
    <>
      {/* Hero */}
      <MKBox
        role="img"
        aria-label="Ramlis Machine - Contato"
        sx={{
          position: "relative",
          pt: NAVBAR_PT,
          minHeight: { xs: 390, md: 400 },
          display: "grid",
          placeItems: "center",
          textAlign: "center",
          color: "#fff",
          backgroundImage: `url(${bgBanner})`,
          backgroundSize: "cover",
          backgroundPosition: { xs: "center", md: "center 35%" },
          backgroundAttachment: { xs: "scroll", md: parallax },
          "@media (prefers-reduced-motion: reduce)": { backgroundAttachment: "scroll" },
          "&::after": {
            content: '""',
            position: "absolute",
            inset: 0,
            background: "linear-gradient(180deg, rgba(0, 14, 8, 0.75) 0%, rgba(0,76,42,.55) 45%, rgba(0,76,42,.75) 100%)",
          },
        }}
      >
        <FadeIn show={loaded} timeout={600} sx={{ position: "relative", zIndex: 1, width: "100%", mt: 5 }}>
          <SectionContainer>
            <MKBox
              sx={{
                display: "inline-flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 0.5,
                px: { xs: 1.5, md: 3 },
                py: { xs: 1, md: 1.25 },
                mx: "auto",
                borderRadius: 2,
                backgroundColor: BRAND.glass,
                backdropFilter: "saturate(1.1) blur(3px)",
                boxShadow: "0 10px 28px rgba(0,76,42,.35)",
              }}
            >
              <MKTypography
                component="h1"
                variant="h3"
                fontWeight="bold"
                color="white"
                mb={0.5}
                sx={{
                  textShadow: "0 2px 6px rgba(0,76,42,.85)",
                }}
              >
                Contato
              </MKTypography>
              <MKTypography
                variant="body1"
                color="white"
                sx={{
                  opacity: 0.95,
                  textShadow: "0 1px 3px rgba(0,76,42,.8)",
                  fontSize: { xs: "0.9rem", md: "1rem" }
                }}
              >
                Solicite um orçamento ou envie seu currículo.
              </MKTypography>
            </MKBox>
          </SectionContainer>
        </FadeIn>
      </MKBox>

      {/* Breadcrumbs */}
      <SectionContainer sx={{ mt: -3, mb: 4 }}>
        <Breadcrumbs routes={[{ label: "Home", route: "/" }, { label: "Contato" }]} />
      </SectionContainer>

      {/* Conteúdo */}
      <Card sx={{
        p: 2,
        mx: { xs: 2, lg: 3 },
        mt: -8,
        mb: 4,
        boxShadow: ({ boxShadows: { xxl } }) => xxl,
        borderRadius: 2,
        overflow: "visible"
      }}>
        <FadeIn show={loaded} timeout={700}>
          <Container sx={{ px: { xs: 1, md: 2 } }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={5}>
                <CompanyInfoCard />
              </Grid>

              <Grid item xs={12} md={7}>
                <Card sx={{
                  p: 0,
                  overflow: "hidden",
                  boxShadow: "0 8px 20px rgba(0,0,0,0.1)"
                }}>
                  <Tabs
                    value={tab}
                    onChange={handleTabChange}
                    variant="fullWidth"
                    textColor="primary"
                    indicatorColor="primary"
                    sx={{
                      "& .MuiTab-root": {
                        py: 2,
                        fontSize: { xs: "0.8rem", sm: "0.9rem" },
                        fontWeight: 600,
                        transition: "all 0.3s ease",
                        "&.Mui-selected": {
                          backgroundColor: "rgba(0,76,42,0.05)"
                        }
                      }
                    }}
                  >
                    <Tab
                      icon={<AssignmentIcon />}
                      iconPosition="start"
                      label={isMobile ? "Orçamento" : "Solicitar orçamento"}
                    />
                    <Tab
                      icon={<WorkIcon />}
                      iconPosition="start"
                      label={isMobile ? "Trabalhe conosco" : "Trabalhe conosco (RH)"}
                    />
                  </Tabs>

                  <div style={{ position: "relative", minHeight: 400 }}>
                    <Fade in={tab === 0} timeout={500} unmountOnExit>
                      <div style={{ padding: 24 }}>
                        <MKTypography variant="h5" fontWeight="medium" gutterBottom color="primary">
                          Solicite um Orçamento
                        </MKTypography>
                        <MKTypography variant="body2" color="text" mb={2}>
                          Preencha o formulário abaixo com os detalhes do seu projeto e anexe o desenho técnico.
                          Nossa equipe retornará com um orçamento em breve.
                        </MKTypography>

                        <MKTypography variant="body2" color="text" mb={3}>
                          Se preferir, envie as informações diretamente para o e-mail{" "}
                          <a
                            href="mailto:contato@ramlismachine.com.br"
                            style={{ color: "#1976d2", textDecoration: "none", fontWeight: 500 }}
                          >
                            contato@ramlismachine.com.br
                          </a>.
                        </MKTypography>
                        <RFQForm />
                      </div>
                    </Fade>

                    <Fade in={tab === 1} timeout={500} unmountOnExit>
                      <div style={{ padding: 24 }}>
                        <MKTypography variant="h5" fontWeight="medium" gutterBottom color="primary">
                          Trabalhe Conosco
                        </MKTypography>
                        <MKTypography variant="body2" color="text" mb={2}>
                          Interessado(a) em fazer parte da nossa equipe? Envie seu currículo e entraremos em contato
                          quando surgirem oportunidades alinhadas ao seu perfil.
                        </MKTypography>

                        <MKTypography variant="body2" color="text" mb={3}>
                          Se preferir, envie seu currículo diretamente para o e-mail{" "}
                          <a
                            href="mailto:contato@ramlismachine.com.br"
                            style={{ color: "#1976d2", textDecoration: "none", fontWeight: 500 }}
                          >
                            contato@ramlismachine.com.br
                          </a>{" "}
                          ou preencha o formulário abaixo.
                        </MKTypography>

                        <CareersForm />
                      </div>
                    </Fade>
                  </div>
                </Card>
              </Grid>
            </Grid>
          </Container>
        </FadeIn>
      </Card>

      <WhatsAppFloat />
    </>
  );
}